/**
 * userService
 * Firestore helpers for `users/{uid}` profile + role documents.
 *
 * Role model:
 * - `viewer`: default on first sign-in
 * - `editor`: dashboard create/update access
 * - `admin`: full dashboard + staff role management
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { assertFirebaseConfigured, db } from '../lib/firebase/config';

export const USER_ROLES = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
};

export function isDashboardRole(role) {
  return role === USER_ROLES.ADMIN || role === USER_ROLES.EDITOR;
}

// Converts Firestore Timestamp/Date-like values to Date for UI consistency.
function toDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  return null;
}

function toMillis(value) {
  const date = toDate(value);
  return date ? date.getTime() : 0;
}

// Normalizes Firestore snapshots into UI-friendly user records.
function normalizeUser(snapshot) {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export async function ensureUserDocument(user) {
  assertFirebaseConfigured();
  const userDocRef = doc(db, 'users', user.uid);
  const userDocSnapshot = await getDoc(userDocRef);

  if (!userDocSnapshot.exists()) {
    // First sign-in bootstrap: create a minimal viewer profile document.
    await setDoc(userDocRef, {
      uid: user.uid,
      email: user.email ?? '',
      displayName: user.displayName ?? '',
      photoURL: user.photoURL ?? '',
      role: USER_ROLES.VIEWER,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  const refreshedSnapshot = await getDoc(userDocRef);
  return { id: refreshedSnapshot.id, ...refreshedSnapshot.data() };
}

export async function getUserDocument(uid) {
  assertFirebaseConfigured();
  const userDocRef = doc(db, 'users', uid);
  const userDocSnapshot = await getDoc(userDocRef);

  if (!userDocSnapshot.exists()) {
    return null;
  }

  return { id: userDocSnapshot.id, ...userDocSnapshot.data() };
}

export async function listUserDocuments() {
  assertFirebaseConfigured();
  const snapshot = await getDocs(collection(db, 'users'));

  return snapshot.docs
    .map(normalizeUser)
    .sort((a, b) => toMillis(b.updatedAt ?? b.createdAt) - toMillis(a.updatedAt ?? a.createdAt));
}

export async function updateUserRole(uid, role) {
  assertFirebaseConfigured();
  const userDocRef = doc(db, 'users', uid);
  await updateDoc(userDocRef, {
    role,
    updatedAt: serverTimestamp(),
  });
}

export async function createStaffUserDocument({
  uid,
  email = '',
  displayName = '',
  role = USER_ROLES.EDITOR,
}) {
  assertFirebaseConfigured();

  const userId = (uid || '').trim();
  if (!userId) {
    throw new Error('UID is required.');
  }

  if (![USER_ROLES.ADMIN, USER_ROLES.EDITOR, USER_ROLES.VIEWER].includes(role)) {
    throw new Error('Invalid role.');
  }

  const userDocRef = doc(db, 'users', userId);
  const snapshot = await getDoc(userDocRef);
  if (snapshot.exists()) {
    throw new Error('A user document with this UID already exists.');
  }

  // This creates the Firestore staff profile/role document only.
  // Auth account creation is handled separately in Firebase Auth workflows.
  await setDoc(userDocRef, {
    uid: userId,
    email: (email || '').trim(),
    displayName: (displayName || '').trim(),
    photoURL: '',
    role,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
